import { useEffect, useMemo } from 'react';
import { HistoryLotCard } from '../components/HistoryLotCard';
import { LotHistoryList } from '../components/LotHistoryList';
import { ProfileHeader } from '../components/ProfileHeader';
import { ReferralPanel } from '../components/ReferralPanel';
import { HISTORY_PAGE_SIZE, useHistory } from '../hooks/useHistory';
import { useReferral } from '../hooks/useReferral';
import { useSession } from '../hooks/useSession';
import { ApiError } from '../lib/apiClient';
import { type HistoryLot, historyLotFromApi } from '../lib/history';
import { historyView } from '../lib/historyView';
import { placeholderBidder } from '../lib/lots';
import { buildAppDeeplink, getTgPhotoUrl, openTgLink } from '../lib/tg';

// `shownCount` / `onShowMore` are lifted into App so the revealed history depth
// survives a drill-down into a lot (this component unmounts) and resets on a
// section change — the same technique the home "Recent wins" list uses.
export function Profile({
  onSelectLot,
  shownCount,
  onShowMore,
}: {
  onSelectLot: (id: string) => void;
  shownCount: number;
  onShowMore: () => void;
}) {
  const session = useSession();
  const referral = useReferral();
  const query = useHistory(true);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = query;

  // Everything fetched so far (all cached pages). The visible slice is carved
  // from this; pages stay cached across collapses so re-expanding is instant.
  const loaded = useMemo<HistoryLot[]>(
    () => (query.data?.pages ?? []).flatMap((page) => page.lots.map(historyLotFromApi)),
    [query.data],
  );
  const lifetimeWins = query.data?.pages[0]?.lifetime_wins ?? 0;
  const totalLots = query.data?.pages[0]?.total_lots ?? 0;
  const isSessionExpired = query.error instanceof ApiError && query.error.status === 401;

  const { visibleCount, canShowMore, shouldFetchNext } = historyView({
    shownCount,
    loadedCount: loaded.length,
    total: totalLots,
    hasNextPage: hasNextPage ?? false,
  });

  // Revealing past the cache pulls the next page; the wider slice renders it once
  // it arrives. Network is touched only when the slice outruns what's loaded.
  useEffect(() => {
    if (shouldFetchNext && !isFetchingNextPage) fetchNextPage();
  }, [shouldFetchNext, isFetchingNextPage, fetchNextPage]);

  const visible = loaded.slice(0, visibleCount);

  return (
    <main className="pb-20">
      {session && (
        <ProfileHeader
          bidder={placeholderBidder(
            session.user.id,
            session.user.first_name,
            session.user.username,
          )}
          photoUrl={getTgPhotoUrl()}
          wins={lifetimeWins}
          refBids={referral?.totalEarned ?? 0}
          index={0}
        />
      )}

      {referral && <ReferralPanel summary={referral} />}

      {query.isPending && (
        <div className="px-4 pt-10 text-center">
          <p className="font-mono text-[11px] tracking-[0.25em] text-fg-muted">LOADING HISTORY…</p>
        </div>
      )}

      {query.isError &&
        (isSessionExpired ? (
          <div className="mx-3 mt-6 border border-edge bg-bg-elev/40 px-4 py-6 text-center">
            <p className="font-mono text-[10px] tracking-[0.25em] text-fg-muted">SESSION EXPIRED</p>
            <p className="text-[13px] text-fg-muted mt-2">
              Your Telegram session timed out. Reopen SkinStep to pick up where you left off.
            </p>
            <button
              type="button"
              onClick={() => openTgLink(buildAppDeeplink())}
              className="mt-3 font-mono text-[11px] text-primary border border-primary/40 px-3 py-1.5 hover:bg-primary/10"
            >
              REOPEN
            </button>
          </div>
        ) : (
          <div className="mx-3 mt-6 border border-danger/40 bg-danger/5 px-4 py-6 text-center">
            <p className="font-mono text-[10px] tracking-[0.25em] text-danger">FAILED TO LOAD</p>
            <p className="text-[13px] text-fg-muted mt-2">Could not reach the auction service.</p>
            <button
              type="button"
              onClick={() => query.refetch()}
              className="mt-3 font-mono text-[11px] text-primary border border-primary/40 px-3 py-1.5 hover:bg-primary/10"
            >
              RETRY
            </button>
          </div>
        ))}

      {!query.isPending &&
        !query.isError &&
        (loaded.length === 0 ? (
          <div className="mx-3 mt-8 border border-dashed border-edge px-4 py-10 text-center bg-bg-elev/30">
            <p className="font-mono text-[10px] tracking-[0.25em] text-fg-muted">NO HISTORY YET</p>
            <p className="text-[13px] text-fg-muted mt-2">
              Bid on a live lot and it shows up here — win or not.
            </p>
          </div>
        ) : (
          <LotHistoryList
            label="Your lots"
            count={totalLots}
            items={visible}
            renderItem={(lot, i) => (
              <HistoryLotCard lot={lot} index={i} onSelect={() => onSelectLot(lot.id)} />
            )}
            canShowMore={canShowMore}
            showMoreCount={Math.min(HISTORY_PAGE_SIZE, totalLots - visibleCount)}
            onShowMore={onShowMore}
            isLoadingMore={isFetchingNextPage}
          />
        ))}
    </main>
  );
}
